/**
 * Tech-debt #4 — Network / customer-info family (OCPP 2.0.1, Session 24):
 * SetNetworkProfile (stored, never switches the live connection) and
 * CustomerInformation (synthetic report — the simulator holds no PII).
 */
import { errorMessage } from '@/lib/errorMessage';
import { OcppV201Adapter } from '@/protocol/v201/OcppV201Adapter';
import { paginateNotifyCustomerInformation } from '@/protocol/v201/actions/notifyCustomerInformation';
import { csmsSession } from '../csmsSessionState';
import { useStationStore } from '../useStationStore';
import type { CsmsCtx, CsmsEvent } from './context';

export function handleSetNetworkProfile(
  event: CsmsEvent<'SetNetworkProfile'>,
  ctx: CsmsCtx,
): void {
  // Store the profile in its slot; reply Accepted. We don't switch the live
  // connection. SetNetworkProfileStatus { Accepted, Rejected, Failed }.
  csmsSession.setNetworkProfile(ctx.stationId, event.configurationSlot, event.connectionData);
  ctx.dispatcher.reply(ctx.messageId, { status: 'Accepted' });
}

export async function handleCustomerInformation(
  event: CsmsEvent<'CustomerInformation'>,
  ctx: CsmsCtx,
): Promise<void> {
  const { adapter, dispatcher, messageId } = ctx;
  // CustomerInformationStatus { Accepted, Rejected, Invalid }. At least one
  // identifier must be present; we hold no PII so `clear` is a no-op and
  // `report` synthesises a small station/customer blob.
  if (!(adapter instanceof OcppV201Adapter)) {
    dispatcher.replyError(messageId, 'NotImplemented', 'CustomerInformation is v2.0.1-only', {});
    return;
  }
  const hasIdentifier =
    event.customerIdentifier !== undefined || event.hasIdToken || event.hasCertificate;
  if (!hasIdentifier) {
    dispatcher.reply(messageId, { status: 'Invalid' });
    return;
  }
  dispatcher.reply(messageId, { status: 'Accepted' });
  if (event.report) {
    const store = useStationStore.getState();
    const txCount = [...store.connectors.values()].filter(
      (c) => c.activeTransactionId !== null,
    ).length;
    const blob = JSON.stringify({
      customer: event.customerIdentifier ?? '(by idToken/certificate)',
      station: { cpid: store.cpid, stationId: store.stationId },
      activeTransactions: txCount,
      note: 'VirtualCharger holds no customer PII; this is a synthetic report.',
    });
    const pages = paginateNotifyCustomerInformation(event.requestId, blob);
    try {
      for (const page of pages) {
        await adapter.notifyCustomerInformation(page).response;
      }
    } catch (err) {
      useStationStore.setState({ lastError: errorMessage(err) });
    }
  }
}
